import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  History, CheckCircle, Clock, XCircle, Mail, Phone,
  RefreshCw, Download, Filter, Search, Calendar,
  Users, Store, DollarSign, TrendingUp,
  AlertTriangle, RotateCcw, Loader2, Ban
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import DashboardHeader from '../components/DashboardHeader';
import BreadcrumbNav from '../components/BreadcrumbNav';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { floorAED } from '../utils/money';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentRecords() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { token } = useAuth();
  
  // State
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [platformFilter, setPlatformFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    paid: 0,
    pending: 0,
    totalAmount: 0
  });

  const getHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  // Fetch all transactions from all platforms
  const fetchAllTransactions = async () => {
    setLoading(true);
    try {
      // Fetch from both platforms
      const [cloudgmRes, businessRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/payments/transactions?platform=cloudgm&limit=100`, { headers: getHeaders() }),
        axios.get(`${BACKEND_URL}/api/payments/transactions?platform=cloudgm_business&limit=100`, { headers: getHeaders() })
      ]);

      const cloudgmData = (cloudgmRes.data.data || []).map(t => ({ ...t, platform: 'cloudgm' }));
      const businessData = (businessRes.data.data || []).map(t => ({ ...t, platform: 'cloudgm_business' }));
      
      // Combine and sort by date
      const allTransactions = [...cloudgmData, ...businessData].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );

      setTransactions(allTransactions);

      // Calculate stats
      const paidTransactions = allTransactions.filter(t => t.payment_status === 'paid');
      const pendingTransactions = allTransactions.filter(t => ['pending', 'processing', 'initiated'].includes(t.payment_status));
      const totalPaidAmount = paidTransactions.reduce((sum, t) => sum + (t.amount || 0), 0);

      setStats({
        total: allTransactions.length,
        paid: paidTransactions.length,
        pending: pendingTransactions.length,
        totalAmount: totalPaidAmount
      });

    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error(language === 'ar' ? 'فشل في جلب السجلات' : 'Failed to fetch records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchAllTransactions();
    }
  }, [token]);

  // Filter transactions
  const filteredTransactions = transactions.filter(t => {
    // Platform filter
    if (platformFilter !== 'all' && t.platform !== platformFilter) return false;
    
    // Status filter
    if (statusFilter !== 'all' && t.payment_status !== statusFilter) return false;
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesName = t.customer_name?.toLowerCase().includes(query);
      const matchesEmail = t.customer_email?.toLowerCase().includes(query);
      const matchesDesc = t.description?.toLowerCase().includes(query);
      if (!matchesName && !matchesEmail && !matchesDesc) return false;
    }
    
    return true;
  });

  // Get status badge — 7 canonical statuses
  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending':    { icon: Clock,          color: 'text-yellow-400',  bg: 'bg-yellow-500/20',  label: language === 'ar' ? 'معلق' : 'Pending' },
      'processing': { icon: Loader2,        color: 'text-blue-400',    bg: 'bg-blue-500/20',    label: language === 'ar' ? 'قيد المعالجة' : 'Processing' },
      'paid':       { icon: CheckCircle,    color: 'text-emerald-400', bg: 'bg-emerald-500/20', label: language === 'ar' ? 'مدفوع' : 'Paid' },
      'failed':     { icon: XCircle,        color: 'text-red-400',     bg: 'bg-red-500/20',     label: language === 'ar' ? 'فشل' : 'Failed' },
      'expired':    { icon: AlertTriangle,  color: 'text-orange-400',  bg: 'bg-orange-500/20',  label: language === 'ar' ? 'منتهي' : 'Expired' },
      'refunded':   { icon: RotateCcw,      color: 'text-violet-400',  bg: 'bg-violet-500/20',  label: language === 'ar' ? 'مسترد' : 'Refunded' },
      'cancelled':  { icon: Ban,            color: 'text-slate-400',   bg: 'bg-slate-500/20',   label: language === 'ar' ? 'ملغي' : 'Cancelled' },
      // Legacy aliases
      'initiated':  { icon: Clock,          color: 'text-yellow-400',  bg: 'bg-yellow-500/20',  label: language === 'ar' ? 'معلق' : 'Pending' },
    };
    
    const config = statusConfig[status] || statusConfig['pending'];
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.color}`}>
        <Icon className="h-3.5 w-3.5" />
        {config.label}
      </span>
    );
  };

  // Get platform badge
  const getPlatformBadge = (platform) => {
    if (platform === 'cloudgm') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-[#d4af37]/20 text-[#d4af37]">
          <Users className="h-3 w-3" />
          {language === 'ar' ? 'العملاء' : 'Customers'}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-purple-500/20 text-purple-400">
        <Store className="h-3 w-3" />
        {language === 'ar' ? 'التجار' : 'Merchants'}
      </span>
    );
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['التاريخ', 'المنصة', 'اسم العميل', 'البريد', 'الهاتف', 'الوصف', 'المبلغ', 'العملة', 'الحالة'];
    const rows = filteredTransactions.map(t => [
      new Date(t.created_at).toLocaleDateString('ar-AE'),
      t.platform === 'cloudgm' ? 'منصة العملاء' : 'منصة التجار',
      t.customer_name,
      t.customer_email || '-',
      t.customer_phone || '-',
      t.description,
      t.amount,
      t.currency,
      t.payment_status
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `payment_records_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success(language === 'ar' ? 'تم تصدير السجلات' : 'Records exported');
  };

  return (
    <div className="min-h-screen bg-[#030303]">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      {/* Header */}
      <DashboardHeader
        title={language === 'ar' ? 'سجل المدفوعات' : 'Payment Records'}
        subtitle={language === 'ar' ? 'جميع المدفوعات من كل المنصات' : 'All payments from all platforms'}
        icon={History}
        iconColor="#d4af37"
        onBack={() => navigate('/finance/asb606')}
      />

      {/* Main Content */}
      <main className="relative z-10">
        {/* Breadcrumb with line */}
        <div className="border-b border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <BreadcrumbNav items={[
              { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
              { label_ar: 'المالية', label_en: 'Finance', path: '/dashboard/finance' },
              { label_ar: 'ASB606 (المالية)', label_en: 'ASB606 (Finance)', path: '/finance/asb606' },
              { label_ar: 'سجل المدفوعات', label_en: 'Payment Records', path: null }
            ]} />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-4 border border-white/10"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#d4af37]/20 flex items-center justify-center">
                  <History className="h-5 w-5 text-[#d4af37]" />
                </div>
                <div>
                  <p className="text-[#71717A] text-xs">{language === 'ar' ? 'إجمالي المعاملات' : 'Total Transactions'}</p>
                  <p className="text-white text-xl font-bold">{stats.total}</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-4 border border-white/10"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-[#71717A] text-xs">{language === 'ar' ? 'مدفوع' : 'Paid'}</p>
                  <p className="text-emerald-400 text-xl font-bold">{stats.paid}</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-4 border border-white/10"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-[#71717A] text-xs">{language === 'ar' ? 'معلق' : 'Pending'}</p>
                  <p className="text-yellow-400 text-xl font-bold">{stats.pending}</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white/[0.02] backdrop-blur-xl rounded-2xl p-4 border border-white/10"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#d4af37]/20 flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-[#d4af37]" />
                </div>
                <div>
                  <p className="text-[#71717A] text-xs">{language === 'ar' ? 'إجمالي المدفوع' : 'Total Paid'}</p>
                  <p className="text-[#d4af37] text-xl font-bold">{floorAED(stats.totalAmount).toFixed(2)}</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Filters Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <History className="h-6 w-6 text-[#d4af37]" />
              {language === 'ar' ? 'السجلات' : 'Records'}
              <span className="text-sm font-normal text-[#71717A]">({filteredTransactions.length})</span>
            </h2>

            <div className="flex items-center gap-3 flex-wrap">
              {/* Search */}
              <div className="relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#71717A]" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={language === 'ar' ? 'بحث...' : 'Search...'}
                  className="w-48 bg-white/5 border-white/10 text-white pr-10 rounded-xl"
                />
              </div>

              {/* Platform Filter */}
              <select
                value={platformFilter}
                onChange={(e) => setPlatformFilter(e.target.value)}
                className="px-4 py-2 bg-[#1a1a2e] border border-white/10 rounded-xl text-white text-sm focus:border-[#d4af37] focus:outline-none cursor-pointer [&>option]:bg-[#1a1a2e] [&>option]:text-white"
              >
                <option value="all">{language === 'ar' ? 'كل المنصات' : 'All Platforms'}</option>
                <option value="cloudgm">{language === 'ar' ? 'منصة العملاء' : 'Customers'}</option>
                <option value="cloudgm_business">{language === 'ar' ? 'منصة التجار' : 'Merchants'}</option>
              </select>

              {/* Status Filter — 7 canonical statuses */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 bg-[#1a1a2e] border border-white/10 rounded-xl text-white text-sm focus:border-[#d4af37] focus:outline-none cursor-pointer [&>option]:bg-[#1a1a2e] [&>option]:text-white"
                data-testid="payment-records-status-filter"
              >
                <option value="all">{language === 'ar' ? 'كل الحالات' : 'All Status'}</option>
                <option value="pending">{language === 'ar' ? 'معلق' : 'Pending'}</option>
                <option value="processing">{language === 'ar' ? 'قيد المعالجة' : 'Processing'}</option>
                <option value="paid">{language === 'ar' ? 'مدفوع' : 'Paid'}</option>
                <option value="failed">{language === 'ar' ? 'فشل' : 'Failed'}</option>
                <option value="expired">{language === 'ar' ? 'منتهي' : 'Expired'}</option>
                <option value="refunded">{language === 'ar' ? 'مسترد' : 'Refunded'}</option>
                <option value="cancelled">{language === 'ar' ? 'ملغي' : 'Cancelled'}</option>
              </select>

              {/* Refresh */}
              <Button
                variant="outline"
                onClick={fetchAllTransactions}
                className="border-white/10 text-white hover:bg-white/5"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>

              {/* Export */}
              <Button
                variant="outline"
                onClick={exportToCSV}
                className="border-white/10 text-white hover:bg-white/5"
              >
                <Download className="h-4 w-4 me-2" />
                {language === 'ar' ? 'تصدير' : 'Export'}
              </Button>
            </div>
          </div>

          {/* Transactions Table */}
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-20 bg-white/[0.02] rounded-xl animate-pulse" />
              ))}
            </div>
          ) : filteredTransactions.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16 bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10"
            >
              <History className="h-16 w-16 mx-auto mb-4 opacity-30 text-[#d4af37]" />
              <h3 className="text-lg font-medium text-white mb-2">
                {language === 'ar' ? 'لا توجد سجلات' : 'No records found'}
              </h3>
              <p className="text-[#71717A]">
                {language === 'ar' ? 'جرب تغيير الفلاتر' : 'Try changing the filters'}
              </p>
            </motion.div>
          ) : (
            <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-start p-4 text-[#71717A] text-sm font-medium">{language === 'ar' ? 'التاريخ' : 'Date'}</th>
                      <th className="text-start p-4 text-[#71717A] text-sm font-medium">{language === 'ar' ? 'المنصة' : 'Platform'}</th>
                      <th className="text-start p-4 text-[#71717A] text-sm font-medium">{language === 'ar' ? 'العميل' : 'Customer'}</th>
                      <th className="text-start p-4 text-[#71717A] text-sm font-medium">{language === 'ar' ? 'الوصف' : 'Description'}</th>
                      <th className="text-start p-4 text-[#71717A] text-sm font-medium">{language === 'ar' ? 'المبلغ' : 'Amount'}</th>
                      <th className="text-start p-4 text-[#71717A] text-sm font-medium">{language === 'ar' ? 'الحالة' : 'Status'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <AnimatePresence>
                      {filteredTransactions.map((transaction, index) => (
                        <motion.tr
                          key={transaction.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ delay: index * 0.02 }}
                          className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                        >
                          <td className="p-4">
                            <div className="flex items-center gap-2 text-white text-sm">
                              <Calendar className="h-3.5 w-3.5 text-[#71717A]" />
                              {new Date(transaction.created_at).toLocaleDateString('ar-AE')}
                            </div>
                            <div className="text-[#71717A] text-xs mt-0.5">
                              {new Date(transaction.created_at).toLocaleTimeString('ar-AE', { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </td>
                          <td className="p-4">
                            {getPlatformBadge(transaction.platform)}
                          </td>
                          <td className="p-4">
                            <div className="text-white text-sm font-medium">{transaction.customer_name}</div>
                            {transaction.customer_email && (
                              <div className="flex items-center gap-1 text-[#71717A] text-xs mt-0.5">
                                <Mail className="h-3 w-3" />
                                {transaction.customer_email}
                              </div>
                            )}
                          </td>
                          <td className="p-4">
                            <div className="text-white text-sm max-w-[200px] truncate">{transaction.description}</div>
                          </td>
                          <td className="p-4">
                            <div className="text-[#d4af37] font-bold">
                              {floorAED(transaction.amount).toFixed(2)} <span className="text-xs font-normal">{transaction.currency}</span>
                            </div>
                          </td>
                          <td className="p-4">
                            {getStatusBadge(transaction.payment_status)}
                          </td>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      </main>
    </div>
  );
}
